import React, { Fragment, useState, useContext, useEffect } from "react";
import { Theme, makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import { Typography, Button } from "@material-ui/core";
import { NavigationRounded, DeleteRounded } from "@material-ui/icons";
import MapboxGl from "mapbox-gl";
import { DesignContext } from "../../context/design";
import { Feature } from "geojson";
import { scaleTextToZoomLevel } from "../../constant/commons";

const useStyle = makeStyles((theme: Theme) => ({
  default: { color: theme.palette.secondary.main },
  active: { color: theme.palette.primary.main },
  spaceInGroup: {
    marginLeft: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  flexMiddle: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  btnSelect: {
    boxShadow: "unset",
    padding: theme.spacing(0.3),
    width: theme.spacing(4),
    height: theme.spacing(4),
    fontFamily: ["Montserrat-Bold", "Kanit-Bold"].join(","),
    minWidth: "unset",
  },
  delete: { color: theme.palette.error.main },
}));

const SelectAnno = () => {
  const classes = useStyle();
  const [active, setactive] = useState<boolean>(false);
  const [selected, setselected] = useState<
    MapboxGl.MapboxGeoJSONFeature | undefined | any
  >(undefined);
  const { map, event, setevent } = useContext(DesignContext);

  const _activeSelected = () => {
    setactive(!active);
  };

  useEffect(() => {
    const mapEvent: any = map;
    const annoLayers = mapEvent
      ?.getStyle()
      .layers.filter((layer: any) => String(layer.id).includes("anno"));

    const mouseEnter = (e: MapboxGl.MapLayerMouseEvent) => {
      e.target.getCanvas().style.cursor = "pointer";
    };
    const mouseLeave = (e: MapboxGl.MapLayerMouseEvent) => {
      e.target.getCanvas().style.cursor = "";
    };

    const mouseClick = (e: MapboxGl.MapLayerMouseEvent) => {
      const features = e.target.queryRenderedFeatures(e.point);
      e.target.getCanvas().style.cursor = "";
      if (features) {
        const annoFeature = features.find((feature) =>
          feature.layer.id.includes("anno")
        );
        setselected(annoFeature);
      }
    };

    if (active && map) {
      annoLayers.map((annoLayer: any) => {
        map.on("mouseenter", annoLayer.id, mouseEnter);
        map.on("mouseleave", annoLayer.id, mouseLeave);
        map.on("click", annoLayer.id, mouseClick);
        return null;
      });
    }
    return () => {
      if (active && map) {
        annoLayers.map((annoLayer: any) => {
          map.off("mouseenter", annoLayer.id, mouseEnter);
          map.off("mouseleave", annoLayer.id, mouseLeave);
          map.off("click", annoLayer.id, mouseClick);
          return null;
        });
      }
    };
  }, [active, map]);

  useEffect(() => {
    const _selectChange = (e: {
      features: Feature[];
      point: MapboxGl.PointLike;
      target: MapboxGl.Map;
      type: "draw.selectionchange";
    }) => {
      const mapEvent: any = e.target;
      const drawEvent = mapEvent?._controls.find(
        (control: any) => control.modes
      );
      if (e.features.length === 0) {
        const drawFeature = drawEvent.get(selected.properties.id);
        mapEvent.getSource(selected.layer.id).setData(drawFeature);
        if (String(selected.layer.id).includes("label")) {
          mapEvent?.setLayoutProperty(selected.layer.id, "text-font", [
            drawFeature.properties["text-font"],
          ]);
          mapEvent?.setLayoutProperty(selected.layer.id, "text-size", [
            "interpolate",
            ["exponential", 2],
            ["zoom"],
            0,
            0,
            scaleTextToZoomLevel(500),
            Number(drawFeature.properties["text-size"]),
          ]);
          mapEvent?.setLayoutProperty(
            selected.layer.id,
            "text-rotate",
            drawFeature.properties["text-rotate"]
          );
          mapEvent?.setPaintProperty(
            selected.layer.id,
            "text-color",
            drawFeature.properties["text-color"]
          );
        } else if (String(selected.layer.id).includes("shape")) {
          if (drawFeature.properties.equip_type === "anno-shape-stroke") {
            mapEvent?.setPaintProperty(
              selected.layer.id,
              "line-color",
              drawFeature.properties["fill-outline-color"]
            );
          } else {
            mapEvent?.setPaintProperty(
              selected.layer.id,
              "fill-color",
              drawFeature.properties["fill-color"]
            );
            mapEvent?.setPaintProperty(
              selected.layer.id,
              "fill-opacity",
              drawFeature.properties["fill-opacity"]
            );
            mapEvent?.setPaintProperty(
              selected.layer.id,
              "fill-outline-color",
              drawFeature.properties["fill-outline-color"]
            );
          }
        }

        const annoLayers = mapEvent
          .getStyle()
          .layers.filter((layer: any) => String(layer.id).includes("anno"));

        const annoSources = annoLayers.map((layer: any) =>
          mapEvent?.getStyle().sources[layer.source].data.id === drawFeature.id
            ? {
                ...layer,
                source: { type: "geojson", data: drawFeature },
                layout: { ...layer.layout, visibility: "visible" },
              }
            : {
                ...layer,
                source: mapEvent.getStyle().sources[layer.source],
              }
        );

        mapEvent.setLayoutProperty(selected.layer.id, "visibility", "visible");
        setevent({
          ...event,
          type: undefined,
          feature: undefined,
          anno: annoSources,
        });
        setselected(undefined);
      }
    };
    if (selected && map) {
      const mapEvent: any = map;
      const drawEvent = mapEvent?._controls.find(
        (control: any) => control.modes
      );

      const annoFeatureSource = mapEvent.getStyle().sources[selected.source]
        .data;
      map.setLayoutProperty(selected.layer.id, "visibility", "none");

      drawEvent.add(annoFeatureSource);
      drawEvent.changeMode(drawEvent.modes.SIMPLE_SELECT, {
        featureIds: [String(selected.properties.id)],
      });

      setactive(false);
      setevent({
        ...event,
        type: selected.properties.equip_type,
        feature: annoFeatureSource,
      });
      mapEvent.on("draw.selectionchange", _selectChange);
    }

    return () => {
      if (selected && map) {
        const mapEvent: any = map;
        const drawEvent = mapEvent?._controls.find(
          (control: any) => control.modes
        );
        drawEvent.deleteAll();
        mapEvent.off("draw.selectionchange", _selectChange);
        drawEvent.changeMode(drawEvent.modes.SIMPLE_SELECT);
      }
    };
  }, [selected, map, setevent]);

  const onDelete = () => {
    const mapEvent: any = map;
    const drawEvent = mapEvent?._controls.find((control: any) => control.modes);

    const delLayer = mapEvent
      .getStyle()
      .layers.find((layer: any) =>
        String(layer.id).includes(String(event.feature?.id))
      );
    if (delLayer) {
      map?.removeLayer(delLayer.id);
      map?.removeSource(delLayer.source);
    }
    drawEvent.deleteAll();
    setevent({
      ...event,
      type: undefined,
      feature: undefined,
      anno: delLayer
        ? event.anno.filter((anNo: any) => anNo.id !== delLayer.id)
        : event.anno,
    });
    setselected(undefined);
  };

  return (
    <Fragment>
      <Typography variant="subtitle2" className={clsx(classes.flexMiddle)}>
        Selection:
      </Typography>
      <Button
        variant="outlined"
        size="small"
        color={active ? "primary" : "default"}
        className={clsx(classes.btnSelect, classes.spaceInGroup)}
        disabled={Boolean(event.type)}
        onClick={_activeSelected}
      >
        <NavigationRounded
          className={clsx(
            active && classes.active,
            !Boolean(event.type) && classes.default
          )}
          style={{ transform: `rotate(295deg)` }}
        />
      </Button>
      <Button
        variant="outlined"
        size="small"
        className={clsx(classes.btnSelect, classes.spaceInGroup)}
        disabled={
          !Boolean(
            Boolean(
              event.type === "anno-label" || event.type?.includes("anno-shape")
            ) && event.feature
          )
        }
        onClick={onDelete}
      >
        <DeleteRounded
          className={clsx(
            Boolean(
              Boolean(
                event.type === "anno-label" ||
                  event.type?.includes("anno-shape")
              ) && event.feature
            ) && classes.delete
          )}
        />
      </Button>
    </Fragment>
  );
};

export default SelectAnno;
